import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DotsThreeOutline, CaretDown, Shield, Info, Flask, ShieldCheck, BookOpen } from '@phosphor-icons/react';

const MORE_LINKS = [
  { to: '/research', label: 'Research', icon: BookOpen, matchPrefix: '/research' },
  { to: '/trials', label: 'Clinical Trials', icon: Flask, matchPrefix: '/trials' },
  { to: '/advocacy', label: 'Advocacy', icon: Shield, matchPrefix: '/advocacy' },
  { to: '/about', label: 'About', icon: Info, matchPrefix: '/about' },
  { to: '/trust', label: 'Trust & Safety', icon: ShieldCheck, matchPrefix: '/trust' },
];

export default function DesktopMoreDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isChildActive = MORE_LINKS.some((link) =>
    location.pathname.startsWith(link.matchPrefix)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border transition-colors ${
          isChildActive
            ? 'bg-pink-100 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700 text-gray-800 dark:text-gray-100'
            : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
        }`}
      >
        <DotsThreeOutline
          size={20}
          weight={isChildActive ? 'fill' : 'regular'}
          className={isChildActive ? 'text-pink-600 dark:text-pink-400' : undefined}
        />
        <span className={isChildActive ? 'font-medium' : undefined}>More</span>
        <CaretDown size={14} className="text-gray-500 dark:text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
          {MORE_LINKS.map(({ to, label, icon: Icon, matchPrefix }) => {
            const active = location.pathname.startsWith(matchPrefix);
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                  active
                    ? 'text-pink-700 dark:text-pink-300 bg-pink-50 dark:bg-pink-950 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Icon
                  size={18}
                  weight={active ? 'fill' : 'regular'}
                  className={active ? 'text-pink-600 dark:text-pink-400' : undefined}
                />
                <span>{label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
